import { useCallback, useEffect, useState } from 'react';
import { api } from '../lib/api';
import { Badge, Button, Card, EmptyState, ErrorAlert, Pagination } from '../components/ui';
import { usePagination } from '../lib/usePagination';

type Any = Record<string, any>;

const STATUS_TONES: Record<string, 'success' | 'warning' | 'danger' | 'neutral'> = {
  APPROVED: 'success', PUBLISHED: 'success', SCHEDULED: 'success',
  READY_FOR_REVIEW: 'warning', GENERATING: 'warning', RESEARCH_READY: 'warning',
  FAILED: 'danger', ARCHIVED: 'neutral',
};

function tone(status: string) {
  return STATUS_TONES[status] ?? 'neutral';
}

export function NieboGuardPackagesSection({ onOpen }: { onOpen: (id: string) => void }) {
  const [packages, setPackages] = useState<Any[]>([]);
  const [topics, setTopics] = useState<Any[]>([]);
  const [detail, setDetail] = useState<Any | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const paged = usePagination(packages, 10);

  const load = useCallback(async () => {
    const [packageResult, topicResult] = await Promise.all([
      api.nieboguard.packages.list(),
      api.nieboguard.topics(),
    ]);
    setPackages(packageResult.packages);
    setTopics(topicResult.topics);
  }, []);

  useEffect(() => { load().catch(setError); }, [load]);

  const run = async (key: string, fn: () => Promise<unknown>, message: string) => {
    setBusy(key); setError(null); setNotice(null);
    try {
      await fn();
      setNotice(message);
      await load();
      if (detail) setDetail(await api.nieboguard.packages.get(detail.package.id));
    } catch (err) {
      setError(err);
    } finally {
      setBusy(null);
    }
  };

  const open = async (id: string) => {
    setBusy(`open-${id}`);
    setError(null);
    try {
      setDetail(await api.nieboguard.packages.get(id));
    } catch (err) {
      setError(err);
    } finally {
      setBusy(null);
    }
  };

  const eligibleTopics = topics.filter((topic) => topic.status === 'READY' && topic.approvalStatus === 'APPROVED');

  return (
    <>
      {error ? <ErrorAlert error={error} /> : null}
      {notice ? <Card><div className="nb-stat-note">{notice}</div></Card> : null}

      <Card
        title="Content Packages — jeden temat, wiele materiałów"
        actions={<span className="nb-stat-note">{packages.length} pakietów</span>}
      >
        <p className="nb-stat-note">Pakiet zawiera domyślnie 4 różne Shorty, 1 Long Video i 2 artykuły wiedzy. Research jest wspólny; każdy materiał ma własny scenariusz, status i publikację.</p>
        <div className="nb-list">
          {eligibleTopics.map((topic) => (
            <div className="nb-item nb-row nb-row--between" key={topic.id}>
              <div>
                <div className="nb-item__title">{topic.title}</div>
                <div className="nb-item__meta"><Badge tone="success">zweryfikowany</Badge><span>{topic.category}</span><span>{topic.language ?? 'pl'}</span></div>
              </div>
              <Button
                size="sm"
                variant="primary"
                disabled={busy === `create-${topic.id}`}
                onClick={() => run(`create-${topic.id}`, () => api.nieboguard.packages.create(topic.id, 'pl'), 'Pakiet utworzony. Wybierz go i uruchom „Generuj wszystko”.')}
              >{busy === `create-${topic.id}` ? 'Tworzę…' : 'Utwórz pakiet'}</Button>
            </div>
          ))}
          {eligibleTopics.length === 0 ? <EmptyState title="Brak zweryfikowanych tematów" hint="Zaakceptuj kandydata w Research Sources i zweryfikuj temat w Edu Shorts." /> : null}
        </div>
      </Card>

      <Card title="Pakiety" actions={<Button size="sm" variant="ghost" onClick={() => load().catch(setError)}>Odśwież</Button>}>
        <div className="nb-list">
          {paged.pageRows.map((pkg) => (
            <div className="nb-item nb-row nb-row--between" key={pkg.id}>
              <div>
                <div className="nb-item__title">{pkg.title}</div>
                <div className="nb-item__meta">
                  <Badge tone={tone(pkg.status)}>{pkg.status}</Badge>
                  <span>{pkg.language}</span><span>Shorty: {pkg.shortCount}</span><span>Long: {pkg.longCount}</span><span>Artykuły: {pkg.articleCount}</span>
                  <span>{pkg.updatedAt}</span>
                </div>
              </div>
              <Button size="sm" onClick={() => open(pkg.id)} disabled={busy === `open-${pkg.id}`}>Otwórz</Button>
            </div>
          ))}
          <Pagination page={paged.page} pageSize={paged.pageSize} total={paged.total} onChange={paged.setPage} />
          {packages.length === 0 ? <EmptyState title="Brak pakietów" hint="Utwórz pakiet z zatwierdzonego tematu." /> : null}
        </div>
      </Card>

      {detail ? <Card
        title={`Pakiet: ${detail.package.title}`}
        actions={<div className="btn-row">
          <Button size="sm" onClick={() => run(`gen-${detail.package.id}`, () => api.nieboguard.packages.generate(detail.package.id), 'Generowanie pakietu zlecone. Odśwież za chwilę.')} disabled={busy === `gen-${detail.package.id}`}>Generuj wszystko</Button>
          <Button size="sm" variant="primary" onClick={() => run(`approve-${detail.package.id}`, () => api.nieboguard.packages.approve(detail.package.id), 'Pakiet zatwierdzony.')} disabled={busy === `approve-${detail.package.id}`}>Zatwierdź pakiet</Button>
          <Button size="sm" onClick={() => run(`queue-${detail.package.id}`, () => api.nieboguard.packages.queue(detail.package.id), 'Zatwierdzone filmy dodane do kolejki publikacji.')} disabled={busy === `queue-${detail.package.id}`}>Kolejkuj filmy</Button>
          <Button size="sm" onClick={() => run(`hub-${detail.package.id}`, () => api.nieboguard.packages.createHub(detail.package.id), 'Utworzono stronę pakietu (DRAFT). Zatwierdź ją w Treści / SEO i wyślij do NieboGuard.')} disabled={busy === `hub-${detail.package.id}`}>Strona pakietu</Button>
          <Button size="sm" variant="ghost" onClick={() => run(`rebuild-${detail.package.id}`, () => api.nieboguard.packages.rebuildArticles(detail.package.id), 'Artykuły przebudowane pod SEO i czytelność; most treści odświeżony.')} disabled={busy === `rebuild-${detail.package.id}`}>Przebuduj artykuły</Button>
          <Button size="sm" variant="ghost" onClick={() => run('sync-hubs', () => api.nieboguard.packages.syncHubs(30), 'Status hubów sprawdzony. Aktywne linki zaktualizowane.')} disabled={busy === 'sync-hubs'}>Odśwież huby</Button>
          <Button size="sm" variant="ghost" onClick={() => setDetail(null)}>Zamknij</Button>
        </div>}
      >
        <div className="nb-item__meta" style={{ marginBottom: 10 }}>
          <Badge tone={tone(detail.package.status)}>{detail.package.status}</Badge>
          <span>research: {detail.package.research?.status ?? '—'}</span>
          <span>pewność: {detail.package.research?.confidence ?? '—'}</span>
          <span>{detail.package.sourceUrls?.join(' · ')}</span>
          {detail.package.metadata?.hubUrl ? <><Badge tone={detail.package.metadata.hubActive ? 'success' : 'warning'}>{detail.package.metadata.hubActive ? 'hub publiczny' : 'hub w review'}</Badge><a href={detail.package.metadata.hubUrl} target="_blank" rel="noreferrer">Strona pakietu ↗</a></> : null}
        </div>
        <div className="nb-list">
          {(detail.items ?? []).map((item: Any) => (
            <div className="nb-item" key={item.id}>
              <div className="nb-row nb-row--between">
                <div>
                  <div className="nb-item__title">
                    <Badge tone="neutral">{item.kind === 'SHORT' ? 'SHORT' : item.kind === 'LONG' ? 'LONG' : 'ARTYKUŁ'}</Badge>{' '}
                    {item.title}
                  </div>
                  <div className="nb-item__meta">
                    <Badge tone={tone(item.status)}>{item.status}</Badge>
                    {item.angle ? <span>{item.angle}</span> : null}
                    {item.productionId ? <span>produkcja: {item.productionId}</span> : null}
                    {item.articleId ? <span>artykuł: {item.articleId}</span> : null}
                    {item.error ? <span style={{ color: 'var(--danger, #b42318)' }}>{item.error}</span> : null}
                  </div>
                </div>
                <div className="btn-row">
                  {item.productionId ? <Button size="sm" variant="ghost" onClick={() => onOpen(item.productionId)}>Podgląd</Button> : null}
                  {item.article ? <Button size="sm" variant="ghost" onClick={() => setDetail({ ...detail, articlePreview: detail.articlePreview === item.id ? null : item.id })}>Artykuł</Button> : null}
                  <Button size="sm" variant="ghost" onClick={() => run(`approve-item-${item.id}`, () => api.nieboguard.packages.approveItem(item.id), 'Element zatwierdzony.')} disabled={busy === `approve-item-${item.id}` || !['READY_FOR_REVIEW', 'SCRIPT_READY'].includes(item.status)}>Zatwierdź</Button>
                  {item.kind !== 'ARTICLE' ? <Button size="sm" onClick={() => run(`queue-item-${item.id}`, () => api.nieboguard.packages.queueItem(item.id), 'Film dodany do kolejki (limit 1 publikacji/platformę/dzień).')} disabled={busy === `queue-item-${item.id}` || item.status !== 'APPROVED'}>Kolejkuj</Button> : null}
                </div>
              </div>
              {detail.articlePreview === item.id && item.article ? (
                <div style={{ marginTop: 10 }}>
                  <div className="nb-item__meta"><span>{item.article.status}</span><span>{item.article.slug}</span><span>{item.article.updatedAt}</span></div>
                  <pre className="nb-code" style={{ maxHeight: 420, overflow: 'auto' }}>{item.article.markdown}</pre>
                </div>
              ) : null}
            </div>
          ))}
        </div>
      </Card> : null}

      <Card title="NieboGuard Knowledge API">
        <p className="nb-stat-note">Gotowe artykuły są dostępne wyłącznie po zatwierdzeniu, przez zabezpieczony endpoint serwer-serwer:</p>
        <pre className="nb-code">{`GET /api/feeds/nieboguard-knowledge/feed?language=pl&limit=50
Authorization: Bearer $NIEBOGUARD_KNOWLEDGE_TOKEN
If-None-Match: <ETag>

GET /api/feeds/nieboguard-knowledge/articles/{id}
GET /api/feeds/nieboguard-knowledge/articles/{id}/versions
POST /api/feeds/nieboguard-knowledge/articles/{id}/ack
{ "version": 1, "action": "IMPORTED" }`}</pre>
        <p className="nb-stat-note">Feed nie zawiera wersji roboczych. Wycofane artykuły pojawiają się w tablicy <code>withdrawn</code>. Kursor i ETag zapewniają idempotentną synchronizację.</p>
      </Card>
    </>
  );
}
